import { useEffect, useState } from 'react'

import { useSesion } from '@/modules/auth/useSesion'
import { pedirUbicacionConReintento } from '@/modules/ejecucion/useVisitas'
import { MapaDelSitio } from '@/modules/programacion/MapaDelSitio'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { Tarjeta } from '@/shared/ui/Tarjeta/Tarjeta'

import { useBodega, useGuardarBodega } from './useBodega'
import estilos from './PaginaBodega.module.css'

/**
 * Dónde está la bodega y hasta dónde llega su geocerca.
 *
 * Es el punto contra el que se valida la devolución de herramientas por QR: si
 * la bodega se muda, aquí se corrige el mapa y el radio, y la devolución empieza
 * a exigir el sitio nuevo sin tocar código. Se puede arrastrar el marcador en el
 * mapa o tomar la ubicación del dispositivo estando parado en la bodega.
 */
export function PaginaBodega() {
  const { puede } = useSesion()
  const { bodega, cargando } = useBodega()
  const guardar = useGuardarBodega()

  const [direccion, setDireccion] = useState('')
  const [latitud, setLatitud] = useState<number | null>(null)
  const [longitud, setLongitud] = useState<number | null>(null)
  const [radio, setRadio] = useState(100)
  const [error, setError] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [ubicando, setUbicando] = useState(false)

  useEffect(() => {
    if (!bodega) return
    setDireccion(bodega.direccion)
    setLatitud(bodega.latitud)
    setLongitud(bodega.longitud)
    setRadio(bodega.radio_m)
  }, [bodega])

  if (cargando || !bodega) return <Cargando mensaje="UBICANDO BODEGA PRINCIPAL..." />

  const sede = bodega // ya no es null: se fija para los closures de abajo
  const editable = puede('maestros', 'editar')
  const centro = { latitud: sede.latitud, longitud: sede.longitud }

  async function usarMiUbicacion() {
    setError(null)
    setAviso(null)
    setUbicando(true)
    const punto = await pedirUbicacionConReintento()
    setUbicando(false)
    if (!punto) {
      setError('El teléfono no dio la ubicación. Actívala e inténtalo de nuevo.')
      return
    }
    setLatitud(punto.latitud)
    setLongitud(punto.longitud)
    setAviso('Ubicación tomada del dispositivo. Revisa el mapa y guarda.')
  }

  async function onGuardar() {
    setError(null)
    setAviso(null)
    if (latitud === null || longitud === null) {
      setError('Falta marcar la ubicación en el mapa.')
      return
    }
    try {
      await guardar.mutateAsync({ id: sede.id, direccion, latitud, longitud, radio_m: radio })
      setAviso('Ubicación de la bodega guardada. La devolución por QR ya usa este punto.')
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo guardar.')
    }
  }

  return (
    <Tarjeta subtitulo="Ubicación de la bodega · con ella se valida la devolución de herramientas por QR">
      {error && (
        <div className={estilos.mensaje}>
          <Aviso tipo="error">{error}</Aviso>
        </div>
      )}
      {aviso && (
        <div className={estilos.mensaje}>
          <Aviso tipo="exito">{aviso}</Aviso>
        </div>
      )}

      <div className={estilos.mapa}>
        <MapaDelSitio
          sitio={latitud !== null && longitud !== null ? { latitud, longitud } : null}
          centro={centro}
          radioM={radio}
          direccion={direccion}
          onMover={editable ? (la, lo) => { setLatitud(la); setLongitud(lo) } : undefined}
        />
      </div>

      <label className={estilos.campo}>
        <span className={estilos.etiqueta}>Dirección</span>
        <input
          type="text"
          className={estilos.entrada}
          value={direccion}
          onChange={(e) => setDireccion(e.target.value)}
          disabled={!editable}
        />
      </label>

      <label className={estilos.campo}>
        <span className={estilos.etiqueta}>Radio de la geocerca (metros)</span>
        <input
          type="number"
          min={20}
          step="10"
          className={estilos.entrada}
          value={radio}
          onChange={(e) => setRadio(Math.max(20, Number(e.target.value)))}
          disabled={!editable}
        />
      </label>

      <p className={estilos.coords}>
        Punto actual: {latitud?.toFixed(6) ?? '—'}, {longitud?.toFixed(6) ?? '—'}
      </p>

      {editable && (
        <div className={estilos.acciones}>
          <Boton variante="secundario" onClick={() => void usarMiUbicacion()} trabajando={ubicando}>
            Usar mi ubicación actual
          </Boton>
          <Boton onClick={() => void onGuardar()} trabajando={guardar.isPending}>
            Guardar
          </Boton>
        </div>
      )}
    </Tarjeta>
  )
}
